import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import axios from "axios";
import {
  Grid,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Card,
  CardContent,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import LeftMenu from "../../components/left-menu";

const CardContainer = styled(Card)(({ theme, selected }) => ({
  border: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  cursor: 'pointer',
  textAlign: 'center',
  transition: 'border 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));
const EmojiTypography = styled(Typography)({
  fontSize: '2rem',
});

export default function NewInquiry() {
  const navigate = useNavigate();
  const passed = useLocation();
  const nowstate = useSelector(state => state);
  const currentSales = nowstate?.user?.sales || '';

  // ---------- helpers ----------
  const genDraftKey = () => `dk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,8)}`;
  const idEq = (a, b) => String(a) === String(b);
  const isOkUpdateNote = (respData) => {
    try {
      if (!respData) return false;
      if (typeof respData.success === 'boolean') return respData.success;
      const payload = respData.data || {};
      return !!(payload.ticket || payload.draftNoteId || payload.draftNote);
    } catch { return false; }
  };

  // ---------- state ----------
  const [contractData, setContractData] = useState(passed.state.post);
  const [showData, setShowData] = useState(passed.state.post);
  const [updateInquiryView, setUpdateInquiryView] = useState(false);
  const [remindOption, setRemindOption] = useState('Yes');
  const [textFieldValue, setTextFieldValue] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [remindDate, setRemindDate] = useState(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000));
  const [isEditable, setIsEditable] = useState({});
  const [error, setError] = useState("");

  // ---------- ticket notes popup ----------
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editedTicketNote, setEditedTicketNote] = useState('');
  const [initialDraftText, setInitialDraftText] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [ticketSaveStatus, setTicketSaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const ticketDebounceRef = useRef(null);
  const dialogOpenRef = useRef(false);

  // create-ticket UX state
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  // one draft per ticket per page session
  const [draftNoteIds, setDraftNoteIds] = useState({});   // ticketId -> noteId
  const [draftKeysByTicket, setDraftKeysByTicket] = useState({}); // ticketId -> draftKey

  useEffect(() => {
    return () => { if (ticketDebounceRef.current) clearTimeout(ticketDebounceRef.current); };
  }, []);
  useEffect(() => { dialogOpenRef.current = ticketDialogOpen; }, [ticketDialogOpen]);

  // ---------- utilities ----------
  const getNewestTicketNote = (ticket) => {
    if (!ticket?.notes || ticket.notes.length === 0) return null;
    const copy = ticket.notes.slice();
    copy.sort((a, b) => new Date(b?.Date || 0) - new Date(a?.Date || 0));
    return copy[0];
  };
  const getSaveBadge = () => {
    switch (ticketSaveStatus) {
      case 'saving': return 'Saving...';
      case 'saved': return 'Saved!';
      case 'error': return 'Error!';
      default: return '';
    }
  };

  // Ensure exactly one draft note exists in THIS ticket (insert once, then reuse)
  const ensureDraftNoteForTicket = async (ticket) => {
    const tId = ticket._id;

    let draftKey = draftKeysByTicket[tId];
    if (!draftKey) {
      draftKey = genDraftKey();
      setDraftKeysByTicket(prev => ({ ...prev, [tId]: draftKey }));
    }

    // already created earlier
    if (draftNoteIds[tId]) {
      setTicketSaveStatus('idle');
      return { draftKey, draftNoteId: draftNoteIds[tId] };
    }

    setTicketSaveStatus('saving');
    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/updateNote`,
        {
          ticketId: tId,
          author: currentSales,
          sales: currentSales,
          draftKey,
          noteDate: new Date().toISOString(),
        }
      );

      if (isOkUpdateNote(data)) {
        const { ticket: updatedTicket, draftNote, draftNoteId } = data.data || {};

        if (!dialogOpenRef.current) return { draftKey, draftNoteId }; // user closed

        if (updatedTicket) {
          setContractData(prev => {
            if (!prev?.tickets) return prev;
            const idx = prev.tickets.findIndex(x => idEq(x._id, updatedTicket._id));
            const next = [...prev.tickets];
            if (idx >= 0) next[idx] = updatedTicket; else next.push(updatedTicket);
            return { ...prev, tickets: next };
          });
          setSelectedTicket(updatedTicket);
        } else if (draftNote) {
          setContractData(prev => {
            if (!prev?.tickets) return prev;
            const idx = prev.tickets.findIndex(x => idEq(x._id, tId));
            if (idx === -1) return prev;
            const t = { ...prev.tickets[idx] };
            t.notes = Array.isArray(t.notes) ? [draftNote, ...t.notes] : [draftNote];
            const next = [...prev.tickets];
            next[idx] = t;
            setSelectedTicket(t);
            return { ...prev, tickets: next };
          });
        }

        if (draftNoteId) setDraftNoteIds(prev => ({ ...prev, [tId]: draftNoteId }));
        setTicketSaveStatus('idle');
        return { draftKey, draftNoteId };
      } else {
        setTicketSaveStatus('error');
        return { draftKey, draftNoteId: null };
      }
    } catch (e) {
      console.error('ensureDraftNoteForTicket error:', e);
      setTicketSaveStatus('error');
      return { draftKey, draftNoteId: null };
    }
  };

  const openTicketDialog = async (ticket) => {
    setTicketSaveStatus('idle');
    setHasTyped(false);
    setInitialDraftText('');
    setEditedTicketNote('');
    setTicketDialogOpen(true);
    setSelectedTicket(ticket);

    const { draftKey, draftNoteId } = await ensureDraftNoteForTicket(ticket);

    const tNow = (contractData?.tickets || []).find(x => idEq(x._id, ticket._id)) || ticket;
    let draftNote = null;
    if (draftNoteId) {
      draftNote = (tNow.notes || []).find(n => idEq(n._id, draftNoteId));
    } else if (draftKey) {
      draftNote = (tNow.notes || []).find(n => String(n?.DraftKey || '') === String(draftKey));
    } else {
      draftNote = getNewestTicketNote(tNow);
    }
    const baseText = draftNote?.Response || '';
    setInitialDraftText(baseText);
    setEditedTicketNote(baseText);
  };

  const closeTicketDialog = () => {
    setTicketDialogOpen(false);
    setSelectedTicket(null);
    setEditedTicketNote('');
    setInitialDraftText('');
    setHasTyped(false);
    setTicketSaveStatus('idle');
  };

  const handleTicketNoteChange = (newVal) => {
    setEditedTicketNote(newVal);
    if (!selectedTicket) return;

    if (!hasTyped && newVal !== initialDraftText) setHasTyped(true);

    const tId = selectedTicket._id;
    const draftKey = draftKeysByTicket[tId];
    if (!draftKey) return;

    // optimistic update
    setContractData(prev => {
      if (!prev?.tickets) return prev;
      const tIdx = prev.tickets.findIndex(t => idEq(t._id, tId));
      if (tIdx === -1) return prev;

      const t = { ...prev.tickets[tIdx] };
      const notes = Array.isArray(t.notes) ? [...t.notes] : [];
      const byId = draftNoteIds[tId]
        ? notes.findIndex(n => n && idEq(n._id, draftNoteIds[tId]))
        : -1;
      let nIdx = byId === -1
        ? notes.findIndex(n => String(n?.DraftKey || '') === String(draftKey))
        : byId;

      if (nIdx === -1) {
        notes.unshift({
          _id: draftNoteIds[tId] || undefined,
          DraftKey: draftKey,
          Draft: true,
          Date: new Date().toISOString(),
          Response: newVal,
          Author: currentSales,
        });
      } else {
        notes[nIdx] = {
          ...notes[nIdx],
          Response: newVal,
          Date: notes[nIdx].Date || new Date().toISOString(),
        };
      }

      const updatedTicket = { ...t, notes };
      const next = [...prev.tickets];
      next[tIdx] = updatedTicket;
      setSelectedTicket(updatedTicket);
      return { ...prev, tickets: next };
    });

    if (ticketDebounceRef.current) clearTimeout(ticketDebounceRef.current);
    ticketDebounceRef.current = setTimeout(async () => {
      setTicketSaveStatus('saving');
      try {
        const { data } = await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/updateNote`,
          {
            ticketId: tId,
            responseText: newVal,
            author: currentSales,
            sales: currentSales,
            draftKey,
          }
        );

        if (!dialogOpenRef.current) return;

        if (isOkUpdateNote(data)) {
          const { ticket: updatedTicket, draftNoteId } = data.data || {};
          if (draftNoteId) setDraftNoteIds(prev => ({ ...prev, [tId]: draftNoteId }));
          if (updatedTicket) {
            setContractData(prev => {
              if (!prev?.tickets) return prev;
              const idx = prev.tickets.findIndex(x => idEq(x._id, updatedTicket._id));
              const next = [...prev.tickets];
              if (idx >= 0) next[idx] = updatedTicket; else next.push(updatedTicket);
              return { ...prev, tickets: next };
            });
            setSelectedTicket(updatedTicket);
          }
          setTicketSaveStatus('saved');
          setTimeout(() => setTicketSaveStatus('idle'), 750);
        } else {
          setTicketSaveStatus('error');
        }
      } catch (e) {
        console.error('updateNote error:', e);
        if (dialogOpenRef.current) setTicketSaveStatus('error');
      }
    }, 500);
  };

  // ---------- create ticket ----------
  const createNewTicket = async () => {
    try {
      setIsCreatingTicket(true);
      const payload = {
        inquiryId: contractData._id,
        title: `Follow-up — ${new Date().toLocaleDateString('id-ID')}`,
        sales: currentSales || contractData.Sales || 'Open',
      };
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/newTicket`, payload);

      // Support multiple response shapes
      const newTicket =
        data?.data?.ticket || data?.ticket || data;

      if (!newTicket || !newTicket._id) {
        throw new Error('Invalid ticket response');
      }

      // update UI and open notes
      setContractData(prev => {
        const prevTickets = Array.isArray(prev?.tickets) ? prev.tickets : [];
        return { ...prev, tickets: [...prevTickets, { ...newTicket, notes: newTicket.notes || [] }] };
      });

      // open dialog so user can start typing
      await openTicketDialog(newTicket);
    } catch (e) {
      console.error('createNewTicket error:', e);
      alert('Gagal membuat Ticket. Coba lagi ya.');
    } finally {
      setIsCreatingTicket(false);
    }
  };

  // ---------- existing handlers ----------
  const handleChange = (event) => setRemindOption(event.target.value);

  const handleCancelClick = () => {
    setIsEditable({ info: false });
    setContractData(showData);
  };

  const handleEditClick = (field) => {
    setContractData(showData);
    setError("");
    setIsEditable(prevState => {
      const newState = { ...prevState };
      for (let key in newState) if (key !== field) newState[key] = false;
      newState[field] = !prevState[field];
      return newState;
    });
  };

  const handleSave = async () => {
    if (!selectedCard) return alert('Mohon pencet Kulitas inquiry saat ini');
    if (remindOption === 'Drop' && textFieldValue.trim() === '') return alert('Mohon Beri alasan kenapa Inquiry mau di Drop');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/updateInqContacted`, {
        id: contractData._id,
        user: currentSales,
        remindOption,
        textFieldValue,
        selectedCard,
        remindDate
      });
      navigate("/listinquiry");
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save data.');
    }
  };

  const updateInquiry = async () => setUpdateInquiryView(true);
  const handleUpdateInquiryViewClose = () => setUpdateInquiryView(false);
  const handleCardClick = (card) => setSelectedCard(card);

  const cards = [
    { id: 'card1', emoji: '🤔', text: 'Belum tau / Masih belum dibalas', score: 5 },
    { id: 'card2', emoji: '😊', text: 'Mungkin ada Potensi', score: 8 },
    { id: 'card3', emoji: '🤩', text: 'Sangat Bagus, Mau langsung Visit / Payment', score: 10 },
    { id: 'card4', emoji: '📵', text: 'Nomor Tidak Valid / Tidak Pernah Dibalas', score: 0 },
    { id: 'card5', emoji: '📍', text: 'Lokasi Tidak Sesuai', score: 3 },
    { id: 'card6', emoji: '📦', text: 'Produk Tidak Sesuai', score: 3 },
    { id: 'card7', emoji: '💸', text: 'Harga Tidak Sesuai', score: 7 },
    { id: 'card8', emoji: '🤬', text: 'Spam / Penipuan', score: -2 },
    { id: 'card9', emoji: '👻', text: 'Sudah 2-3X FU tidak pernah Respon Lagi', score: 1 },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const optionsDate = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const optionsTime = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
    const formattedDate = new Intl.DateTimeFormat('id-ID', optionsDate).format(date);
    const formattedTime = new Intl.DateTimeFormat('id-ID', optionsTime).format(date);
    return `${formattedDate} (${formattedTime})`;
  };

  function checkCustomerStatus(contractData) {
    if (contractData.PaidPILink && contractData.PaidPILink.includes("Yes")) {
      return "Sudah Jadi Customer";
    }
    switch (contractData.newStat) {
      case "New":
        return "Belum Follow Up";
      case "Drop":
        return "Sudah di Drop";
      default:
        return "Belum Jadi Customer";
    }
  }

  const getNextStepText = () => {
    if (!contractData.remindDate) return "N/A";
    if (contractData.remindDate === "Drop") return "Inquiry Drop dan tidak akan di remind";
    return dayjs(contractData.remindDate).format('DD/MM/YYYY');
  };

  const handleFieldChange = (field, value) => {
    setContractData(prevState => ({ ...prevState, [field]: value }));
  };

  const EditInq = async () => {
    if (contractData.Kualitas && contractData.Phone && contractData.Iklan) {
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/updateInq`, { contractData });
        setShowData(contractData);
      } catch (error) {
        setError(`Error: ${error.message}`);
      }
    } else {
      setError("Ada data yang belum kamu isi");
    }
  };

  const hasTickets = Array.isArray(contractData?.tickets) && contractData.tickets.length > 0;

  return (
    <Grid container>
      {/* Dialog: Update Inquiry */}
      <Dialog open={updateInquiryView} onClose={handleUpdateInquiryViewClose}>
        <DialogTitle id="alert-dialog-title">
          <Button onClick={handleUpdateInquiryViewClose} autoFocus>
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Grid>
            <Typography><b>
              Update baru untuk ({contractData.Nama ?? "Nama Tidak Tersedia"}) di ({contractData.Phone ?? "Nomor Telepon Tidak Tersedia"})?
            </b></Typography>
          </Grid>
          <Grid sx={{ mt: 2 }}>
            Bagaimana Kualitas Inquiry Sekarang?
            <Grid container spacing={2}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={2.4} key={card.id}>
                  <CardContainer onClick={() => handleCardClick(card)} selected={selectedCard?.id === card.id}>
                    <CardContent>
                      <EmojiTypography variant="h1">{card.emoji}</EmojiTypography>
                      <Typography variant="body1">{card.text}</Typography>
                    </CardContent>
                  </CardContainer>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid sx={{ mt: 10 }}>
              Apa masih mau di remind lagi?
              <Grid container xs={11} sx={{ justifyContent: 'space-between' }}>
                <Grid xs={3}>
                  <FormControl fullWidth>
                    <Select labelId="remind-label" id="remind-select" value={remindOption} onChange={(e)=>setRemindOption(e.target.value)}>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="Drop">Drop</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={6}>
                  {remindOption === 'Yes' && (
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      label="Choose Date"
                      value={remindDate}
                      onChange={setRemindDate}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </LocalizationProvider>
          <Grid sx={{ mt: 5 }}>
            Catatan / Notes
            <TextField
              multiline
              fullWidth
              rows={4}
              value={textFieldValue}
              onChange={(e) => setTextFieldValue(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleUpdateInquiryViewClose} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ticket Notes */}
      <Dialog open={ticketDialogOpen} onClose={closeTicketDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Ticket Notes {selectedTicket?.title ? `— ${selectedTicket.title}` : ''}
          </Typography>
          <IconButton onClick={closeTicketDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>All Notes</Typography>
          {selectedTicket?.notes?.length ? (
            [...selectedTicket.notes]
              .sort((a, b) => new Date(b?.Date || 0) - new Date(a?.Date || 0))
              .map((n, idx) => (
                <Paper
                  key={n?._id || idx}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    border: idx === 0 ? '2px solid #1976d2' : '1px solid #ddd',
                    backgroundColor: idx === 0 ? 'rgba(25,118,210,0.06)' : 'inherit'
                  }}
                  variant="outlined"
                >
                  <Typography variant="caption" color="text.secondary">
                    {n?.Date ? new Date(n.Date).toLocaleString() : '(no date)'}
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{n?.Response || '(no response)'}</Typography>
                  {n?.MGMTNotes && (
                    <Typography sx={{ mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                      {n.MGMTNotes}
                    </Typography>
                  )}
                </Paper>
              ))
          ) : (
            <Typography color="text.secondary">(No notes)</Typography>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Today’s Note <small style={{ marginLeft: 8 }}>{getSaveBadge()}</small>
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            value={editedTicketNote}
            onChange={(e) => handleTicketNoteChange(e.target.value)}
          />
        </DialogContent>
      </Dialog>

      {/* Page Layout */}
      <Grid item xl={0.5} lg={0.5} md={0.5} sm={3.2} className="leftmenucolor">
        <LeftMenu />
      </Grid>
      <Grid item xl={11.5} lg={11.5} md={11.5} sm={8.8} sx={{ p: 10 }}>
        <Grid container>
          <Grid item xs={9}>
            <Grid container alignItems="center">
              <Grid item xs={1}>
                <AccountCircleIcon style={{ fontSize: '5vw' }} />
              </Grid>
              <Grid item xs={5}>
                {isEditable.name ? (
                  <Grid container>
                    <TextField
                      value={contractData.Nama}
                      onChange={(e) => handleFieldChange('Nama', e.target.value)}
                      autoFocus
                    />
                    <Button onClick={() => { setShowData(contractData); setIsEditable({ info: false }); EditInq(); }}>
                      Save
                    </Button>
                    <IconButton onClick={handleCancelClick}>
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                ) : (
                  <Grid container>
                    <Typography fontSize="25px"><b>{contractData.Nama ?? "Nama Tidak Tersedia"}</b></Typography>
                    <IconButton onClick={() => handleEditClick('name')}>
                      <EditIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={4}>
                <Typography fontSize="25px">{contractData.Phone ?? "Nomor Telepon Tidak Tersedia"}</Typography>
              </Grid>
              <Grid xs={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    const phoneNumber = contractData.Phone;
                    const message = '';
                    const encodedMessage = encodeURIComponent(message);
                    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                >
                  <WhatsAppIcon />
                </Button>
              </Grid>
            </Grid>
            <Divider sx={{ borderBottomWidth: 2, borderColor: "black" }} />

            <Grid container alignItems="center" sx={{ mt: 1, mb: 1 }}>
              <Grid item xs={6}>
                <Typography>Status</Typography>
                <b>{checkCustomerStatus(contractData)}</b>
              </Grid>
              <Grid xs={6}> <Typography>Currently Assigned To</Typography> <b>{(!contractData.Sales || contractData.Sales === "OpenforAll") ? "Open" : contractData.Sales}</b> </Grid>
            </Grid>

            <Divider sx={{ borderBottomWidth: 2, borderColor: "black" }} />
            <Grid container>
              <Grid xs={6}>
                <Typography fontSize="30px" sx={{ mt: 5, mb: 1 }}><b>Info</b></Typography>
                <Grid xs={12} sx={{ ml: 2 }}>
                  <Typography fontSize="20px">
                    <b>Apakah Pernah buat PI:</b> {contractData.PIlink ?? "tidak"}
                  </Typography>
                  {isEditable.info ? (
                    <>
                      <Typography fontSize="20px">
                        <b>Produk:</b>
                        <TextField
                          value={contractData.ProdukInterest}
                          onChange={(e) => handleFieldChange('ProdukInterest', e.target.value)}
                          fullWidth
                          multiline
                        />
                      </Typography>
                      <Typography fontSize="20px">
                        <b>Lokasi:</b>
                        <TextField
                          value={contractData.LokasiInterest}
                          onChange={(e) => handleFieldChange('LokasiInterest', e.target.value)}
                          fullWidth
                          multiline
                        />
                      </Typography>
                      <Button onClick={() => { setShowData(contractData); setIsEditable({ info: false }); EditInq(); }}>
                        Save
                      </Button>
                      <IconButton onClick={handleCancelClick}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography fontSize="20px">
                        <b>Produk:</b> {Array.isArray(contractData.ProdukInterest) ? contractData.ProdukInterest.join(', ') : (contractData.ProdukInterest ? String(contractData.ProdukInterest) : 'N/A')}
                      </Typography>
                      <Typography fontSize="20px">
                        <b>Lokasi:</b> {Array.isArray(contractData.LokasiInterest) ? contractData.LokasiInterest.join(', ') : (contractData.LokasiInterest ? String(contractData.LokasiInterest) : 'N/A')}
                      </Typography>
                      <IconButton onClick={() => {setContractData(showData); handleEditClick('info');}}>
                        <EditIcon />
                      </IconButton>
                    </>
                  )}
                  {contractData.chatLink ? (
                    <Grid xs={12} sx={{ mb: 5, mt: 5, display: 'flex' }}>
                      <Button variant="contained" onClick={() => window.open(contractData.chatLink, '_blank')}>
                        Cek Chat Qontak/LC
                      </Button>
                    </Grid>
                  ) : (
                    <Grid xs={12} sx={{ mb: 5, mt: 5, display: 'flex' }}>
                      <Button variant="contained" disabled>
                        Cek Chat #1
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid xs={6}>
                <Typography fontSize="30px" sx={{ mt: 5, mb: 1 }}><b>Next Step</b></Typography>
                <Typography fontSize="30px">{getNextStepText()}</Typography>
              </Grid>
            </Grid>
            <Divider sx={{ borderBottomWidth: 2, borderColor: "black" }} />

            <Grid item xs={12} sx={{ mb: 5, mt: 5, display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="success" onClick={updateInquiry}>
                Update Inquiry
              </Button>
            </Grid>
            <Divider sx={{ borderBottomWidth: 2, borderColor: "black" }} />

            {/* Lead History -> tickets for this inquiry */}
            <Grid sx={{ display: 'flex', mt: 3, justifyContent: 'center' }} container>
              <Typography textAlign="center" fontSize="25px"><b>Lead History</b></Typography>

              {!hasTickets ? (
                <Paper sx={{ mt: 3, p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Belum ada Ticket untuk inquiry ini.
                  </Typography>
                  <Button variant="contained" onClick={createNewTicket} disabled={isCreatingTicket}>
                    {isCreatingTicket ? 'Creating…' : 'Create Ticket'}
                  </Button>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><b>Date Modified</b></TableCell>
                        <TableCell><b>Stat</b></TableCell>
                        <TableCell><b>Sales</b></TableCell>
                        <TableCell><b>Keterangan</b></TableCell>
                        <TableCell><b>Action</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contractData.tickets.map((t, i) => {
                        const newest = getNewestTicketNote(t);
                        const modifiedAt = newest?.Date || t?.createdAt;
                        const keterangan = newest?.Response || '(no notes)';
                        return (
                          <TableRow
                            key={t._id || i}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => openTicketDialog(t)}
                          >
                            <TableCell>{formatDate(modifiedAt) || ''}</TableCell>
                            <TableCell>{t?.Marked || t?.title || ''}</TableCell>
                            <TableCell>{t?.Sales || ''}</TableCell>
                            <TableCell>{keterangan}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => { e.stopPropagation(); openTicketDialog(t); }}
                              >
                                View Notes
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Typography>Timeline Summary</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
